import { Observable, Subject } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { take, takeUntil } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { QueryCompanies } from '../../models/query-companies';
import { faSignOutAlt } from '@fortawesome/free-solid-svg-icons';
import { PermissionsEnum } from 'src/app/core/enums/permissions.enum';
import { CompanyService } from 'src/app/core/services/company.service';
import { AssociateCompany } from 'src/app/core/models/associate-company';
import { QueryCompaniesAction } from '../../actions/query-companies.action';
import { PermissionsService } from 'src/app/core/services/permissions.service';
import { SessionCompanies } from 'src/app/core/actions/session-companies.action';
import { CompanyPreview } from 'src/app/core/interfaces/company-preview.interface';
import { InviteUserDialogComponent } from '../../components/invite-user-dialog/invite-user-dialog.component';
import { LeaveCompanyDialogComponent } from '../../components/leave-company-dialog/leave-company-dialog.component';
import { CompanySelectorComponent } from 'src/app/shared/components/company-selector/company-selector.component';
import { formatarCnpj } from 'src/app/shared/utils/cnpj.utils';

@Component({
  selector: 'app-companies',
  templateUrl: './companies.component.html',
  styleUrls: ['./companies.component.scss']
})
export class CompaniesComponent implements OnInit, OnDestroy {
  @ViewChild('paginator') paginator: MatPaginator;

  count: number;
  public modules = [];
  query: QueryCompanies;
  faSignOutAlt = faSignOutAlt;
  destroy$ = new Subject<any>();
  companiesSession: CompanyPreview[] = [];
  public modulePermission = PermissionsEnum;
  dataSource = new MatTableDataSource<AssociateCompany>([]);
  displayedColumns: string[] = ['id', 'companyName', 'cnpj', 'myRole', 'country', 'status', 'actions'];

  queryCompanies$: Observable<QueryCompanies>;
  private sessionCompanies$: Observable<CompanyPreview[]>;

  constructor(
    private readonly queryCompaniesStore: Store<{
      queryCompanies: QueryCompanies;
    }>,
    private readonly dialog: MatDialog,
    private readonly companyService: CompanyService,
    private readonly permissions: PermissionsService,
    private readonly queryCompaniesState: Store<QueryCompanies>,
    private readonly sessionCompaniesState: Store<{
      sessionCompanies: CompanyPreview[];
    }>,
    private readonly selectCompany: CompanySelectorComponent
  ) {
    this.queryCompanies$ = this.queryCompaniesStore.pipe(select('queryCompanies'));
    this.sessionCompanies$ = this.sessionCompaniesState.pipe(select('sessionCompanies'));
  }

  ngOnInit(): void {
    this.modules = this.permissions.getPermissionStorage();
    this.queryCompanies$.pipe(takeUntil(this.destroy$)).subscribe(async (newQuery) => {
      if (!this.sameQuery(newQuery) && newQuery) {
        console.log('QueryCompaniesComponent: queryCompanies$', newQuery);
        this.query = newQuery;
        if (this.query.pageIndex == 0) {
          this.paginator?.firstPage();
        }
        await this.getCompanies();
      }
    });
    this.sessionCompanies$.subscribe((companies) => {
      this.companiesSession = companies ? [...companies] : [];
    });
  }

  protected workAsCompany(company): void {
    this.selectCompany.dispatchCompany(company);
  }

  async getCompanies() {
    try {
      let response = await this.companyService.getAssociateCompanies(this.query);
      if (response.companyCount > 0 && response.companies.length == 0) {
      } else {
        this.count = response.companyCount;
        this.dataSource.data = response.companies;
      }
    } catch {
      this.count = 0;
      this.dataSource.data = [];
    }
  }

  sameQuery(newQuery: QueryCompanies): boolean {
    return (
      newQuery &&
      this.query?.companyName === newQuery?.companyName &&
      this.query?.profileCode === newQuery?.profileCode &&
      this.query?.cnpj === newQuery?.cnpj &&
      this.query?.pageIndex === newQuery?.pageIndex &&
      this.query?.pageSize === newQuery?.pageSize
    );
  }

  pageChange(event) {
    let query: QueryCompanies = {
      companyName: this.query.companyName,
      profileCode: this.query.profileCode,
      pageIndex: event.pageIndex,
      pageSize: event.pageSize
    };
    this.queryCompaniesState.dispatch(QueryCompaniesAction(query));
  }

  inviteUser(idCompany: number): void {
    const dialogRef = this.dialog.open(InviteUserDialogComponent, {
      data: idCompany,
      disableClose: true
    });

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((result) => {
        if (result) {
        }
      });
  }
  leaveCompanyDialog(company: AssociateCompany) {
    const dialogRef = this.dialog.open(LeaveCompanyDialogComponent, {
      data: { idCompany: company.id, companyName: company.companyName },
      disableClose: true
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: boolean) => {
        if (result) {
          this.removeCompanyFromList(company);
          this.removeCompanyFromState(company);
        }
      });
  }
  removeCompanyFromList(company: AssociateCompany) {
    let index = this.dataSource.data.findIndex((c) => c.id === company.id);
    if (index >= 0) {
      let data = [...this.dataSource.data];
      data.splice(index, 1);
      this.dataSource.data = data;
      if (data.length === 0) {
        this.pageChange(0);
      }
    }
  }

  removeCompanyFromState(company: AssociateCompany) {
    let index = this.companiesSession.findIndex((c) => c.id === company.id);
    if (index >= 0) {
      this.companiesSession.splice(index, 1);
      this.sessionCompaniesState.dispatch(SessionCompanies(this.companiesSession));
    }
  }

  formatCnpj(cnpj?: string): string {
    return formatarCnpj(cnpj);
  }

  currentCompany(companyId: number) {
    const company = JSON.parse(localStorage.getItem('current-company'));
    return company.id === companyId;
  }

  ngOnDestroy() {
    this.destroy$.next(true);
  }
}
